import { captureEvent } from '@/instrumentation-client'
import type { NativeAuthFailure } from '@/lib/native-auth-failure'

/**
 * Política de fallo del login social: si se le dice algo al usuario y qué se manda a
 * telemetría. Vive fuera de `auth-screen` porque es una decisión de producto, no de
 * render, y porque la pantalla ya está sobre el umbral de complejidad de CodeScene:
 * toda la ramificación de esta decisión va aquí para no engordarla.
 */

// Narrowing a estas claves (en vez de `string`) permite pasar el traductor tipado de
// next-intl directamente: un traductor sobre más claves es asignable a uno que pide menos.
export type SocialSignInErrorKey =
  | 'apple.error'
  | 'apple.noAccount'
  | 'google.error'
  | 'google.noAccount'

interface SocialSignInCopyConfig {
  failedEvent: string
  errorKey: SocialSignInErrorKey
  noAccountKey: SocialSignInErrorKey
}

interface SocialSignInResult {
  error?: string
  nativeFailure?: NativeAuthFailure
}

export interface ReportSocialSignInFailureInput {
  result: SocialSignInResult
  config: SocialSignInCopyConfig
  translate: (key: SocialSignInErrorKey) => string
  locale: string
  variant: string
  showError: (message: string) => void
}

/**
 * `result.error` solo llega del OAuth web. El login nativo manda `nativeFailure` sin
 * copy para que el idioma salga de i18n y no del castellano incrustado en
 * `auth-operations`.
 */
function resolveMessage({ result, config, translate }: ReportSocialSignInFailureInput): string {
  if (result.error) return result.error

  return translate(
    result.nativeFailure?.outcome === 'no_account' ? config.noAccountKey : config.errorKey
  )
}

/**
 * No hace nada cuando el usuario descartó la hoja del sistema: ni banner de error ni
 * evento de fallo. Contarlo como fallo inflaba la tasa de error del funnel de auth y le
 * decía al usuario que algo se había roto cuando solo había cambiado de idea — que es
 * justo lo que se reportó como «el login de Google no funciona».
 */
export function reportSocialSignInFailure(input: ReportSocialSignInFailureInput): void {
  const { result, config, locale, variant, showError } = input

  if (result.nativeFailure?.outcome === 'cancelled') return

  const message = resolveMessage(input)

  captureEvent(config.failedEvent, {
    success: false,
    error: message,
    // El código del plugin nativo es lo único que distingue «sin cuenta de Google» de
    // «sin Play Services» de un fallo real. Antes moría en un console.error dentro del
    // WebView de un build de Play, donde no lo ve nadie.
    reason_code: result.nativeFailure?.code,
    failure_outcome: result.nativeFailure?.outcome,
    locale,
    variant
  })
  showError(message)
}
